import React, { useState } from 'react';
import './LoginWizard.scss';
import {
    Box,
    Button,
    FormControlLabel,
    IconButton,
    InputAdornment,
    MenuItem,
    MobileStepper,
    Radio,
    RadioGroup,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { post } from '@pages/Preferences/api';
import { useNavigate } from 'react-router-dom';
import { BiSave } from 'react-icons/bi';
import { BinRed, EditPencil, ForwardIconGrey, ForwardIconWhite } from './icons';

export const LoginWizard: React.FC = () => {
    interface Question {
        question: string;
        options?: string[];
        isTextField?: boolean; // Flag to indicate if it's a text field
        isCustomField?: boolean;
    }

    const questions: Question[] = [
        {
            question: "What's your age?",
            options: ['< 15 years', '15 - 25 years', '26 - 35 years', '35 - 45 years', '> 50 years'],
        },
        {
            question: 'What’s your investment experience?',
            options: ['< 1 year', '2 - 5 years', '5 - 8 years', '9 - 10 years', '> 10 years'],
        },
        {
            question: 'What’s your financial goal?',
            options: ['Preserving Capital', 'Generating Income', 'Growing Capital', 'Maximizing Tax efficiency'],
        },
        {
            question: 'Preferred time horizon for investment?',
            options: ['Short Term', 'Medium Term', 'Long Term'],
        },
        {
            question: 'Risk tolerance?',
            options: ['Low', 'Medium', 'High'],
        },
        {
            question: 'Return expectations?',
            options: ['Conservative', 'Moderate', 'Aggressive'],
        },
        {
            question: 'Write asset breakdown?',
            isTextField: true,
        },
    ];

    const industries = [
        'Technology',
        'Healthcare',
        'Finance',
        'Real Estate',
        'Consumer Goods',
        'Energy',
        'Utilities',
        'Telecommunications',
        'Automotive',
        'Retail',
    ];

    const securities = [
        'Stocks',
        'Bonds',
        'Mutual Funds',
        'ETFs',
        'Options',
        'Futures',
        'Cryptocurrency',
        'Commodities',
        'REITs',
        'Treasury Bills',
    ];

    const [activeStep, setActiveStep] = useState<number>(0);
    const [answers, setAnswers] = useState<Record<number, any>>({});
    const [type, setType] = useState<string>('security'); // "Industry" or "Security"
    const [selectedItem, setSelectedItem] = useState<string>('');
    const [percentage, setPercentage] = useState<number | string>('');
    const [list, setList] = useState<{ name: string; percentage: number }[]>([]);
    const [editIndex, setEditIndex] = useState<number | null>(null);
    const [editValue, setEditValue] = useState('');
    const [showStepper, setShowStepper] = useState<boolean>(false);
    const navigate = useNavigate();

    const [totalPercentage, setTotalPercentage] = useState<number>(0);
    const [errorMessage, setErrorMessage] = useState<string>('');

    const handleStartWizard = () => {
        setShowStepper(true);
    };

    const handleSkip = () => {
        navigate('/account-detail');
    };

    const handleAdd = () => {
        if (selectedItem && percentage) {
            const newPercentage = Number(percentage); // Convert percentage to a number
            const newTotalPercentage = totalPercentage + newPercentage;

            if (newTotalPercentage > 100) {
                setErrorMessage('Total percentage cannot exceed 100%');
                return;
            }

            setErrorMessage('');

            setList((prevList) => {
                // Check if the item already exists
                const existingItemIndex = prevList.findIndex((item) => item.name === selectedItem);

                if (existingItemIndex !== -1) {
                    // If it exists, update the percentage by adding the new percentage
                    const updatedList = [...prevList];
                    updatedList[existingItemIndex].percentage += newPercentage; // Use newPercentage here
                    return updatedList;
                } else {
                    // If it doesn't exist, add the new item to the list
                    return [...prevList, { name: selectedItem, percentage: newPercentage }];
                }
            });

            setTotalPercentage(newTotalPercentage); // Update total percentage
            setSelectedItem(''); // Reset selectedItem
            setPercentage(''); // Reset percentage
        } else {
            if (!selectedItem) {
                setErrorMessage(`Please select a ${type}`);
            } else {
                setErrorMessage('Please enter percentage.');
            }
        }
    };

    const handleEdit = (index: number, currentPercentage: number) => {
        setEditIndex(index);
        setEditValue(currentPercentage.toString());
    };

    const handleSave = (index: number) => {
        const updatedPercentage = parseFloat(editValue) || 0;
        const currentItem = list[index];
        const newTotalPercentage = totalPercentage - currentItem.percentage + updatedPercentage;

        if (newTotalPercentage > 100) {
            setErrorMessage('Total percentage cannot exceed 100%');
            return;
        }

        setErrorMessage('');

        const updatedItems = [...list];
        updatedItems[index].percentage = updatedPercentage;
        setList(updatedItems);
        setTotalPercentage(newTotalPercentage);
        setEditIndex(null);
    };

    const handleRemove = (index: number) => {
        setList((prevList) => prevList.filter((_, i) => i !== index));
    };

    const maxSteps = questions.length;

    const handleBack = () => {
        if (activeStep > 0) {
            setActiveStep((prevStep) => prevStep - 1);
        }
    };

    const handleAnswerChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        field?: 'profit' | 'loss',
    ) => {
        const value = event.target.value;
        if (field) {
            setAnswers({
                ...answers,
                [activeStep]: { ...(answers[activeStep] || {}), [field]: value },
            });
        } else {
            setAnswers({ ...answers, [activeStep]: value });
        }
    };

    const [isAnswered, setIsAnswered] = useState(false);

    const handleLabelClick = (value: string) => {
        // Set the answer for the current step
        setAnswers((prevAnswers) => ({ ...prevAnswers, [activeStep]: value }));
        setIsAnswered(true); // Mark the question as answered
    };

    const goToNextStep = () => {
        if (isAnswered && activeStep < maxSteps - 1) {
            setActiveStep((prevStep) => prevStep + 1);
            setIsAnswered(false); // Reset after moving to the next step
        }
    };

    const sendToBackend = async () => {
        const preferences = questions.map((question, index) => question.question);
        let preferencesResponse = questions.map((question, index) => answers[index] || '');
        preferencesResponse[6] = JSON.stringify(list);
        const userStorage = localStorage.getItem('User');
        const userObject = JSON.parse(userStorage || '{}');

        const requestBody = {
            UserId: userObject?.id,
            Preferences: preferences,
            PreferencesResponse: preferencesResponse,
        };

        try {
            let req = await post(requestBody);
            if (req) {
                navigate('/account-detail');
            }
        } catch (error) {}
    };

    return (
        <div className='login-container'>
            <div className='left-image-wizard'>
                <img
                    src='left.png'
                    alt='Left Section Image'
                />
            </div>
            <div className='right-image-wizard'>
                {!showStepper ? (
                    <Box className='button-box'>
                        <Box mt={2}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    backgroundColor: '#446A90',
                                    padding: 2,
                                    borderRadius: 2,
                                    boxShadow: 3,
                                    height: '154px',
                                    width: '460px',
                                }}
                            >
                                <Box>
                                    <Typography
                                        fontSize='24px'
                                        fontWeight='bold'
                                        color='white'
                                    >
                                        Add your preferences now
                                    </Typography>
                                    <Typography
                                        variant='body2'
                                        color='white'
                                        textAlign='left'
                                        marginTop='4px'
                                    >
                                        Personalize your experience right away.
                                    </Typography>
                                </Box>
                                <IconButton
                                    aria-label='Go'
                                    onClick={() => handleStartWizard()}
                                >
                                    <ForwardIconWhite />
                                </IconButton>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    backgroundColor: 'white',
                                    padding: 2,
                                    marginTop: '50px',
                                    borderRadius: 2,
                                    boxShadow: 3,
                                    height: '154px',
                                    width: '460px',
                                }}
                            >
                                <Box>
                                    <Typography
                                        fontSize='24px'
                                        fontWeight='bold'
                                        color='#446A90'
                                    >
                                        Add later
                                    </Typography>
                                    <Typography
                                        variant='body2'
                                        color='#446A90'
                                        textAlign='left'
                                        marginTop='4px'
                                    >
                                        Skip and customize preferences later.
                                    </Typography>
                                </Box>
                                <IconButton
                                    aria-label='Go'
                                    onClick={() => handleSkip()}
                                >
                                    <ForwardIconGrey />
                                </IconButton>
                            </Box>
                        </Box>
                    </Box>
                ) : (
                    <Box className='question-box'>
                        {/* Question */}
                        <Typography
                            variant='h6'
                            gutterBottom
                            className='margin-bottom-40'
                        >
                            {questions[activeStep].question}
                        </Typography>

                        {/* Options or Text Field */}
                        {questions[activeStep].isTextField ? (
                            <Box>
                                <Box
                                    display='flex'
                                    flexDirection='column'
                                    gap={2}
                                >
                                    <RadioGroup
                                        value={type}
                                        onChange={(e) => setType(e.target.value)}
                                        sx={{
                                            '.MuiFormControlLabel-root': {
                                                width: 'max-content',
                                            },
                                        }}
                                    >
                                        <FormControlLabel
                                            value='security'
                                            control={
                                                <Radio
                                                    sx={{
                                                        '&.Mui-checked': {
                                                            '.MuiSvgIcon-root': {
                                                                color: '#0066A6',
                                                            },
                                                        },
                                                    }}
                                                />
                                            }
                                            label='Select by Security (e.g., TSLA, AAPL, etc.)'
                                        />
                                        <FormControlLabel
                                            value='industry'
                                            control={
                                                <Radio
                                                    sx={{
                                                        '&.Mui-checked': {
                                                            '.MuiSvgIcon-root': {
                                                                color: '#0066A6',
                                                            },
                                                        },
                                                    }}
                                                />
                                            }
                                            label='Select by Industry (e.g., Tech, Healthcare, Energy)'
                                        />
                                    </RadioGroup>
                                    {errorMessage && <div style={{ color: 'red' }}>{errorMessage}</div>}
                                    <div className='choose-list-header'>Choose from the list</div>
                                    <div className='security-percentage-div'>
                                        <Select
                                            value={selectedItem}
                                            onChange={(e) => setSelectedItem(e.target.value)}
                                            variant='outlined'
                                            fullWidth
                                            displayEmpty
                                            className='dropdown-security-percentage-div'
                                        >
                                            <MenuItem
                                                value=''
                                                disabled
                                            >
                                                Select a {type.toLowerCase()}
                                            </MenuItem>
                                            {(type === 'Industry' ? industries : securities).map((item, index) => (
                                                <MenuItem
                                                    key={index}
                                                    value={item}
                                                >
                                                    {item}
                                                </MenuItem>
                                            ))}
                                        </Select>

                                        <TextField
                                            fullWidth
                                            type='number'
                                            value={percentage}
                                            onChange={(e) => setPercentage(e.target.value)}
                                            placeholder='Enter % here'
                                            variant='outlined'
                                            className='input-profit'
                                            margin='normal'
                                            InputProps={{
                                                startAdornment: <InputAdornment position='start'>%</InputAdornment>,
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '&:after': {
                                                        borderRight: '2px solid #ccc',
                                                    },
                                                    '.MuiInputBase-inputAdornedStart': {
                                                        padding: '9.5px 14px',
                                                    },
                                                },
                                                '& .MuiInputBase-input': {
                                                    borderLeft: '1px solid #ccc',
                                                    background: 'white',
                                                    paddingLeft: '10px',
                                                },
                                            }}
                                        />
                                    </div>
                                    <Button
                                        variant='contained'
                                        onClick={handleAdd}
                                        className='add-security-percentage-button'
                                    >
                                        Add
                                    </Button>
                                </Box>

                                {/* Display List */}
                                <Box mt={2}>
                                    {list.length > 0 && (
                                        <>
                                            <div className='allocations-list-header'>Allocations list</div>
                                            <ul className='list-row-allocations'>
                                                {list.map((item, index) => (
                                                    <li
                                                        key={index}
                                                        className='list-row-entry-alloctions'
                                                    >
                                                        {item.name}{' '}
                                                        {editIndex === index ? (
                                                            <TextField
                                                                size='small'
                                                                value={editValue}
                                                                onChange={(e) => setEditValue(e.target.value)}
                                                                className='small-text-field-percentage'
                                                            />
                                                        ) : (
                                                            <span className='margin-left-auto'>{`${item.percentage}%`}</span>
                                                        )}
                                                        {editIndex === index ? (
                                                            // If editing, show a Save button
                                                            <IconButton
                                                                size='small'
                                                                color='primary'
                                                                onClick={() => handleSave(index)}
                                                            >
                                                                <BiSave />
                                                            </IconButton>
                                                        ) : (
                                                            // Otherwise, show an Edit button
                                                            <IconButton
                                                                size='small'
                                                                color='secondary'
                                                                onClick={() => handleEdit(index, item.percentage)}
                                                            >
                                                                <EditPencil />
                                                            </IconButton>
                                                        )}
                                                        <IconButton
                                                            size='small'
                                                            color='secondary'
                                                            onClick={() => handleRemove(index)}
                                                        >
                                                            <BinRed />
                                                        </IconButton>
                                                    </li>
                                                ))}
                                            </ul>
                                        </>
                                    )}
                                </Box>

                                <div className='button-bottom-final-step'>
                                    <Button
                                        onClick={handleBack}
                                        className='last-back-button-stepper'
                                    >
                                        Back
                                    </Button>
                                    <Button
                                        variant='contained'
                                        onClick={() => {
                                            sendToBackend();
                                        }}
                                        className='next-button-stepper-finish'
                                    >
                                        Finish
                                    </Button>
                                </div>
                            </Box>
                        ) : questions[activeStep].isCustomField ? (
                            <Box>
                                <TextField
                                    fullWidth
                                    type='number'
                                    value={answers[activeStep]?.profit || ''}
                                    onChange={(e) => handleAnswerChange(e, 'profit')}
                                    placeholder='Enter % here'
                                    variant='outlined'
                                    className='input-profit'
                                    margin='normal'
                                    InputProps={{
                                        startAdornment: <InputAdornment position='start'>Profit</InputAdornment>,
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '&:after': {
                                                borderRight: '2px solid #ccc',
                                            },
                                        },
                                        '& .MuiInputBase-input': {
                                            borderLeft: '1px solid #ccc',
                                            paddingLeft: '10px',
                                        },
                                    }}
                                />

                                <TextField
                                    fullWidth
                                    type='number'
                                    value={answers[activeStep]?.loss || ''}
                                    onChange={(e) => handleAnswerChange(e, 'loss')}
                                    placeholder='Enter % here'
                                    variant='outlined'
                                    className='input-loss'
                                    margin='normal'
                                    InputProps={{
                                        startAdornment: <InputAdornment position='start'>Loss</InputAdornment>,
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '.MuiInputAdornment-root': {
                                                width: '48px',
                                            },
                                        },
                                        '& .MuiInputBase-input': {
                                            borderLeft: '1px solid #ccc',
                                            paddingLeft: '10px',
                                        },
                                    }}
                                />

                                <Button
                                    variant='contained'
                                    onClick={() => {
                                        if (activeStep < maxSteps - 1) {
                                            setActiveStep((prevStep) => prevStep + 1);
                                        }
                                    }}
                                    className='next-button-stepper-finish'
                                >
                                    Finish
                                </Button>
                            </Box>
                        ) : (
                            <RadioGroup
                                name={`question-${activeStep}`}
                                value={answers[activeStep] || ''}
                                className='option-group'
                            >
                                {questions[activeStep].options?.map((option, index) => (
                                    <div
                                        key={index}
                                        className={`option ${answers[activeStep] === option ? 'checked' : ''}`}
                                        onClick={() => {
                                            handleLabelClick(option);
                                            goToNextStep();
                                        }}
                                    >
                                        <input
                                            type='radio'
                                            id={`option-${index}`}
                                            value={option}
                                            checked={answers[activeStep] === option}
                                            onChange={() => {}}
                                        />
                                        <label htmlFor={`option-${index}`}>{option}</label>
                                    </div>
                                ))}
                            </RadioGroup>
                        )}

                        {/* Navigation Buttons */}
                        <Box className='navigation-buttons'>
                            {!(activeStep === 0 || activeStep === 6) && (
                                <Button
                                    onClick={handleBack}
                                    className='next-button-stepper'
                                >
                                    Back
                                </Button>
                            )}
                        </Box>

                        {/* Stepper Dots */}
                        <MobileStepper
                            variant='dots'
                            steps={maxSteps}
                            position='static'
                            activeStep={activeStep}
                            className='stepper-dots'
                            nextButton={<></>} // Hide default navigation
                            backButton={<></>} // Hide default navigation
                        />
                    </Box>
                )}
            </div>
        </div>
    );
};
